<template>
  <div>
    <canvas 
      ref="canvas"
      :width="width"
      :height="height"
      :class="cursor"
      @dragover.prevent
      @mousedown="!readOnly && onDragStart($event)"
      @touchstart="!readOnly && onDragStart($event)"
      @mouseup="!readOnly && onDragEnd($event)"
      @touchend="!readOnly && onDragEnd($event)"
      @mousemove="!readOnly && onMouseMove($event)"
      @touchmove="!readOnly && onMouseMove($event)"
    />
  </div>
</template>

<script lang="ts">
import Vue, { VueConstructor, PropType } from "vue";

/**
 * Point
 */
export type Point = {
  /**
   * x
   */
  x: number;
  /**
   * y
   */
  y: number;
};

/**
 * Size
 */
type Size = {
  /**
   * Height
   */
  height: number;
  /**
   * Width
   */
  width: number;
};

/**
 * Avatar Settings
 */
export type AvatarSettings = Size & Point & {
  /**
   * Radius
   */
  radius: number;
  /**
   * Angle
   */
  angle: number;
  /**
   * Sacle
   */
  scale: number;
  /**
   * Canvas Width
   */
  canvasWidth: number;
  /**
   * Canvas Height
   */
  canvasHeight: number;
};

type AvatarState = Size & Point & {
  // Previous mouse/touch position
  mx: number | null,
  my: number | null,
  // Mouse drag state
  drag: boolean,
  // Image resource
  image: CanvasImageSource | null
}

export default (Vue as VueConstructor<
  Vue & {
    $refs: {
      canvas: InstanceType<typeof HTMLCanvasElement>;
    };
  }
>).extend({
  data: () => ({
    // resizeObserver: null as ResizeObserver | null,
    cursor: "cursor-pointer",
    color: "rgba(0, 0, 0, 0.5)",
    isImageReady: false,
    // Image stats
    state: { 
      x: 0,
      y: 0,
      mx: null,
      my: null,
      height: 0,
      width: 0,
      drag: false,
      image: null
    } as AvatarState
  }),
  props: {
    image: {
      type: File,
      default: undefined
    },
    imageUrl: {
      type: String,
      default: undefined
    },
    settings: {
      type: Object as PropType<AvatarSettings>,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    canvas(): HTMLCanvasElement {
      return this.$refs.canvas;
    },
		context(): CanvasRenderingContext2D | null {
      return this.canvas?.getContext("2d");
    },
    width(): number {
      return this.settings.canvasWidth;
    },
    height(): number {
      return this.settings.canvasHeight;
    },
    radius(): number {
      return this.settings.radius;
    },
    radian(): number {
      return this.settings.angle * Math.PI / 180;
    },
    scale(): number {
      return Math.min(3, Math.max(1, this.settings.scale));
    },
    imagePosition(): Point {
      return this.state as Point;
    },
    canvasRatio(): number {
      return this.width / this.height;
    }
  },
  methods: {
    setImage(img: File | string | undefined) {
      this.clear();
      if (typeof img == "object") {
        this.initImage(img);
      } else if (typeof img == "string") {
        this.loadImage(img);
      } else {
        this.isImageReady = false;
        this.state = { 
          x: 0,
          y: 0,
          drag: false,
          image: null
        } as AvatarState;
      }
    },
    initImage(file: File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.loadImage(e.target?.result ?? null);
      };
      reader.readAsDataURL(file);
    },
    loadImage(img: string | ArrayBuffer | null) {
      if (!img) return;
      const image = new Image();
      const self = this;
      image.onload = function(){
        self.onImageReady(image);
      }
      if (typeof img == "string") {
        image.src = img;
      }
    },
    onImageReady(image: HTMLImageElement) {
      const imageSize = this.getInitialSize(image.width, image.height)
      this.state.image = image;
      this.state.x = 0;
      this.state.y = 0;
      this.state.drag = false;
      this.state.width = imageSize.width;
      this.state.height = imageSize.height;
      this.isImageReady = true;
      this.cursor = this.readOnly ? 'cursor-pointer' : 'cursor-grab';

      if (this.settings.width && this.settings.height) {
        this.state.x = this.settings.x;
        this.state.y = this.settings.y;
        const clamped = this.getCroppingRect();
        this.state.x = clamped.x;
        this.state.y = clamped.y;
      } else {
        const position = {
          x: (this.width - imageSize.width) / 2,
          y: (this.height - imageSize.height) / 2
        } as Point;

        this.state.x = position.x;
        this.state.y = position.y;

        // Update new position. Report the image's true natural pixel size
        // (not the canvas-fitted display size) so the backend can correctly
        // interpret x/y/scale against the actual uploaded photo.
        this.$emit("update:settings", Object.assign(this.settings, { ...position, width: image.width, height: image.height }));
      }
    },
    getInitialSize(width: number, height: number) {
      // Scale the image to fit entirely within the canvas, preserving aspect ratio.
      const containScale = Math.min(this.width / width, this.height / height);

      return {
        width: width * containScale,
        height: height * containScale
      }
    },
    paint() {
      if (!this.context) return;
      this.context.save();
      if (!this.readOnly) {
        // Draw circular mask (dims the area outside the crop circle)
        this.context.translate(0, 0);
        this.context.fillStyle = this.color;
        // clamp border radius between zero (perfect rectangle) and half the size without borders (perfect circle or "pill")
        this.context.beginPath();
        // Draw circle
        this.context.arc(this.width / 2, this.height / 2, this.radius, 0, Math.PI * 2);
        // Draw overlay rectangle
        this.context.rect(this.width, 0, -this.width, this.height);
        this.context.closePath();
        this.context.fill('evenodd');
      }

      // Draw Image
      if (this.state.image) {
        const position = this.calculatePosition();
        const size = this.calculateSize();

        // Translate for rotation
        this.context.translate(this.width / 2, this.height / 2);
        this.context.rotate(this.radian);
        this.context.translate(-(this.width / 2), -(this.height / 2));
        // Scale
        this.context.scale(1, 1);
        // Draw image
        this.context.globalCompositeOperation = 'destination-over';
        this.context.drawImage(this.state.image, position.x, position.y, size.width, size.height);
      }

      this.context.restore();
    },
    redraw() {
      if (!this.isImageReady) return;
      this.context?.clearRect(0, 0, this.width, this.height);
      this.paint();
    },
    getXScale() {
      const imageAspect = this.state.height / this.state.width;
      return Math.min(1, this.canvasRatio / imageAspect);
    },
    getYScale() {
      const imageAspect = this.state.width / this.state.height;
      return Math.min(1, this.canvasRatio / imageAspect);
    },
    getCroppingRect() {
      const position = this.imagePosition;

      const width = this.scale * this.state.width;
      const height = this.scale * this.state.height;

      const croppingRect = {
        x: position.x,
        y: position.y,
        width,
        height,
      };

      // If the (scaled) image is larger than the canvas in a dimension, keep it
      // covering that dimension (0 to canvas-size-minus-image-size). If it's
      // smaller, keep it centered with equal margin on both sides so it can't
      // be dragged off to one side.
      const xMin = width >= this.width ? this.width - width : (this.width - width) / 2;
      const xMax = width >= this.width ? 0 : (this.width - width) / 2;
      const yMin = height >= this.height ? this.height - height : (this.height - height) / 2;
      const yMax = height >= this.height ? 0 : (this.height - height) / 2;

      return {
        ...croppingRect,
        x: Math.max(xMin, Math.min(croppingRect.x, xMax)),
        y: Math.max(yMin, Math.min(croppingRect.y, yMax)),
      }
    },
    calculatePosition(): Point {
      const croppingRect = this.getCroppingRect();

      let x = croppingRect.x;
      let y = croppingRect.y;

      return {
        x,
        y
      } as Point;
    },
    calculateSize(): Size {
      return {
        width: this.state.width * this.scale,
        height: this.state.height * this.scale
      } as Size;
    },
    onDragStart(e: Event) {
      e.preventDefault();
      this.state.drag = true;
      this.state.mx = null;
      this.state.my = null;
      this.cursor = 'cursorGrabbing';
    },
    onDragEnd(_e: Event) {
      if (this.state.drag) {
        this.state.drag = false;
        this.cursor = 'cursorPointer';
        // Update settings
        let position = this.calculatePosition();
        this.$emit("update:settings", Object.assign(this.settings, position));
      }
    },
    onMouseMove(e: MouseEvent | TouchEvent) {
      if (this.state.drag === false) {
        return;
      }
      
      e.preventDefault() // Stop scrolling on iOS Safari

      const mousePositionX = (e instanceof TouchEvent) ? e.targetTouches[0].pageX : e.clientX;
      const mousePositionY = (e instanceof TouchEvent) ? e.targetTouches[0].pageY : e.clientY;

      let rotate = this.settings.angle;
      rotate %= 360
      rotate = rotate < 0 ? rotate + 360 : rotate

      if (this.state.mx && this.state.my) {
        const mx = mousePositionX - this.state.mx;
        const my = mousePositionY - this.state.my;

        let { x: lastX, y: lastY } = this.getCroppingRect();

        // helpers to calculate vectors
        const toRadians = (degree: number) => degree * (Math.PI / 180);
        const cos = Math.cos(toRadians(rotate));
        const sin = Math.sin(toRadians(rotate));

        const x = lastX + mx * cos + my * sin;
        const y = lastY + -mx * sin + my * cos;

        const position = {
          x: x,
          y: y,
        };

        this.state.x = position.x;
        this.state.y = position.y;
      }

      this.state.mx = mousePositionX;
      this.state.my = mousePositionY;
    },
    clear() {
      this.state = { 
        x: 0,
        y: 0,
        mx: null,
        my: null,
        height: 0,
        width: 0,
        drag: false,
        image: null
      };
    }
  },
  watch: {
    image(val: File | undefined) {
      this.setImage(val);
    },
    imageUrl(val: string | undefined) {
      this.setImage(val);
    },
    isImageReady() {
      this.redraw();
    },
    radian() {
      this.redraw();
    },
    scale() {
      // Zooming can push the current position out of bounds; re-clamp it
      // and persist the corrected value so saved x/y stay consistent with scale.
      if (this.isImageReady) {
        const clamped = this.getCroppingRect();
        this.state.x = clamped.x;
        this.state.y = clamped.y;
        this.$emit("update:settings", Object.assign(this.settings, { x: clamped.x, y: clamped.y }));
      }
      this.redraw();
    },
    imagePosition: {
      handler() {
        this.redraw();
      },
      deep: true
    }
  },
  mounted() {
    this.paint();
    if (this.image) this.initImage(this.image);
    else if (this.imageUrl) this.loadImage(this.imageUrl);
  }
});
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-grab {
  cursor: grab;
  cursor: -webkit-grab;
  cursor: -moz-grab;
}
.cursor-grabbing {
  cursor: grabbing;
  cursor: -webkit-grabbing;
  cursor: -moz-grabbing;
}
</style>
